import type { JawAssemblyConfiguration } from '../../interfaces/VersaBuilt.Interface';

export interface SetupWorkholding {
	jaw: JawAssemblyConfiguration | null;
	vise: string | null;
}

/** Same rule as the scheduler's cycle-time sum: this setup machines soft jaws,
 *  not the part, so it has no jaw slot of its own. */
export function isSoftJawSetup(setupName: string | undefined) {
	return (setupName ?? '').toLowerCase().includes('soft jaw');
}

/** Stock-prep soft-jaw machining setup (cuts soft jaws for stock prep). */
export function isStockPrepSoftJawSetup(setupName: string | undefined) {
	return (setupName ?? '').toLowerCase().includes('stock prep soft jaw');
}

/** Stock prep setup that machines the stock (not the soft-jaw machining variant). */
export function isStockPrepSetup(setupName: string | undefined) {
	const name = (setupName ?? '').toLowerCase();
	return name.includes('stock prep') && !name.includes('stock prep soft jaw');
}

/**
 * Display order for CAM Program Data setup tabs:
 * stock-prep soft jaw → stock prep → remaining setups in export order.
 */
export function orderSetupsForDisplay<T extends { name?: string }>(setups: T[]): T[] {
	const stockPrepSoftJaw: T[] = [];
	const stockPrep: T[] = [];
	const rest: T[] = [];
	for (const setup of setups) {
		if (isStockPrepSoftJawSetup(setup?.name)) {
			stockPrepSoftJaw.push(setup);
		} else if (isStockPrepSetup(setup?.name)) {
			stockPrep.push(setup);
		} else {
			rest.push(setup);
		}
	}
	return [...stockPrepSoftJaw, ...stockPrep, ...rest];
}

/** Part-machining setups that use `setup_1/2/3` jaw slots (not stock prep / soft jaw). */
export function isPartMachiningSetup(setupName: string | undefined) {
	return (
		!isSoftJawSetup(setupName) &&
		!isStockPrepSetup(setupName) &&
		!isStockPrepSoftJawSetup(setupName)
	);
}

/**
 * Pairs each CAM setup with the export's `setup_<n>_*` jaw and vise. The slots
 * number the part setups only, in export order, so soft-jaw and stock-prep
 * setups map to `null`.
 */
export function workholdingBySetup(
	setupNames: (string | undefined)[],
	slots: SetupWorkholding[]
): (SetupWorkholding | null)[] {
	let partSetupIndex = 0;
	return setupNames.map((name) => {
		if (!isPartMachiningSetup(name)) return null;
		return slots[partSetupIndex++] ?? null;
	});
}
